import * as React from "react";
import {useEffect, useState} from "react";
import {Button, ButtonGroup} from "semantic-ui-react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-text";
import "ace-builds/src-noconflict/theme-monokai";

export interface CsvViewerProps {
    blob: any;
}

type ViewMode = 'table' | 'raw';

export function CsvViewer({blob}: CsvViewerProps) {
    const [headers, setHeaders] = useState<string[]>([]);
    const [rows, setRows] = useState<string[][]>([]);
    const [rawText, setRawText] = useState<string>('');
    const [viewMode, setViewMode] = useState<ViewMode>('table');

    useEffect(() => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            setRawText(text);
            parseCsv(text);
        };
        reader.readAsText(blob, 'utf-8');
    }, [blob]);

    function parseCsv(text: string) {
        const lines = text.split(/\r?\n/);
        const firstLine = lines[0] ?? '';
        const delimiter = detectDelimiter(firstLine);

        const sheetData = lines
            .filter(line => line.trim() !== '')
            .map(line => parseCSVLine(line, delimiter));

        if (sheetData.length === 0) {
            setHeaders([]);
            setRows([]);
            return;
        }

        const headerRow = sheetData[0];
        const colCount = headerRow.length;
        const dataRows = sheetData.slice(1).map(row => {
            const normalized: string[] = [];
            for (let i = 0; i < colCount; i++) {
                normalized.push(row[i] ?? '');
            }
            return normalized;
        });

        setHeaders(headerRow);
        setRows(dataRows);
    }

    return (
        <div style={{display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0}}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '6px 8px',
                borderBottom: '1px solid #e0e0e0',
                flexShrink: 0,
            }}>
                <span style={{color: '#666', fontSize: '0.85em'}}>
                    {headers.length > 0 && `${headers.length} columns · ${rows.length} rows`}
                </span>
                <ButtonGroup size="small">
                    <Button primary={viewMode === 'table'} onClick={() => setViewMode('table')}>Table</Button>
                    <Button primary={viewMode === 'raw'} onClick={() => setViewMode('raw')}>Raw</Button>
                </ButtonGroup>
            </div>

            {viewMode === 'table' ? (
                <div style={{flex: 1, overflow: 'auto', minHeight: 0}}>
                    <table style={{
                        borderCollapse: 'collapse',
                        width: 'max-content',
                        fontSize: '0.875em',
                        tableLayout: 'auto',
                    }}>
                        <thead>
                        <tr>
                            <th style={thNumberStyle}>#</th>
                            {headers.map((header, i) => (
                                <th key={i} style={thStyle}>{header || <em style={{color: '#aaa'}}>Column {i + 1}</em>}</th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {rows.map((row, rowIdx) => (
                            <tr key={rowIdx} style={{background: rowIdx % 2 === 0 ? '#fff' : '#f9f9f9'}}>
                                <td style={tdNumberStyle}>{rowIdx + 1}</td>
                                {row.map((cell, colIdx) => (
                                    <td key={colIdx} style={tdStyle}>{cell}</td>
                                ))}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div style={{flex: 1, position: 'relative', minHeight: 0}}>
                    <AceEditor
                        theme="monokai"
                        mode="text"
                        value={rawText}
                        readOnly={true}
                        width="100%"
                        height="100%"
                        style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0}}
                        setOptions={{useWorker: false}}
                    />
                </div>
            )}
        </div>
    );
}

const thStyle: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    background: '#2d2d2d',
    color: '#fff',
    padding: '5px 10px',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    borderRight: '1px solid #444',
    fontWeight: 600,
    zIndex: 1,
};

const thNumberStyle: React.CSSProperties = {
    ...thStyle,
    color: '#aaa',
    width: '40px',
    minWidth: '40px',
    textAlign: 'center',
};

const tdStyle: React.CSSProperties = {
    padding: '4px 10px',
    borderBottom: '1px solid #eee',
    borderRight: '1px solid #eee',
    whiteSpace: 'nowrap',
};

const tdNumberStyle: React.CSSProperties = {
    ...tdStyle,
    color: '#aaa',
    textAlign: 'center',
    fontSize: '0.8em',
    background: '#f5f5f5',
    userSelect: 'none',
};

function detectDelimiter(firstLine: string): string {
    const candidates = [';', '|', '\t', ','];
    let best = ',';
    let bestCount = 0;
    for (const sep of candidates) {
        const count = firstLine.split(sep).length - 1;
        if (count > bestCount) {
            bestCount = count;
            best = sep;
        }
    }
    return best;
}

/** RFC 4180-compliant CSV line parser — all values remain strings. */
function parseCSVLine(line: string, delimiter: string): string[] {
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
            if (ch === '"') {
                if (line[i + 1] === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                current += ch;
            }
        } else {
            if (ch === '"') {
                inQuotes = true;
            } else if (ch === delimiter) {
                fields.push(current);
                current = '';
            } else {
                current += ch;
            }
        }
    }
    fields.push(current);
    return fields;
}